import React, { useMemo, useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { asDate, formatMoney } from '../lib/formatters';
import {
  assignDriver,
  controlTowerApproveAdjustment,
  controlTowerCancelOrder,
  controlTowerSendToDifferentStore,
  linkOrderSendMessage,
  updateOrderStage,
} from '../lib/actions';
import { buildLinkDecisionContext, generateLinkSmartSuggestions } from '../lib/linkDecisionEngine';
import { buildLinkPredictionMap, summarizeLinkPredictions } from '../lib/linkPredictionEngine';
import { rankRecoveryActions, summarizeBestRecoveryAction } from '../lib/linkRecoveryEngine';

const FALLBACK_STORES = ['District Six', 'Parow'];
const CLOSED_STAGES = ['archived', 'delivered', 'cancelled', 'complete', 'completed'];
const STAGE_LABELS = {
  waiting: 'Waiting',
  awaiting_store: 'Waiting',
  packing: 'Packing',
  ready: 'Ready',
  ready_for_driver: 'Ready',
  collected: 'Collected',
  out_for_delivery: 'On route',
  on_route: 'On route',
  delivered: 'Delivered',
  archived: 'Archived',
  issue: 'Issue',
  paused: 'Paused',
};

function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function firstText(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function getDate(value) {
  return asDate(value) || null;
}

function minutesSince(value, now) {
  const date = getDate(value);
  if (!date) return 0;
  return Math.max(0, Math.floor((now - date.getTime()) / 60000));
}

function fmtMins(value) {
  const mins = Math.max(0, Number(value || 0));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function money(value) {
  return formatMoney(Number(value || 0));
}

function titleCase(value) {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function itemName(item = {}) {
  return firstText(item.name, item.productName, item.title, item.sku, 'Item');
}

function itemQty(item = {}) {
  return Number(item.qty ?? item.quantity ?? item.count ?? 1) || 1;
}

function itemPrice(item = {}) {
  const direct = Number(item.lineTotal ?? item.total ?? item.amount ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const unit = Number(item.price ?? item.unitPrice ?? item.sellPrice ?? item.value ?? 0) || 0;
  return unit * itemQty(item);
}

function orderItems(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return safeArray(order.items).length ? order.items : safeArray(raw.items);
}

function orderTotal(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const direct = Number(order.total ?? raw.total ?? order.orderValue ?? raw.orderValue ?? order.amount ?? raw.amount ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  return orderItems(order).reduce((sum, item) => sum + itemPrice(item), 0);
}

function customerName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return firstText(order.customerName, raw.customerName, raw.customer?.name, order.customer?.name, 'Customer');
}

function customerPhone(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return firstText(order.customerPhone, raw.customerPhone, raw.customer?.phone, order.customer?.phone, 'No phone captured');
}

function deliveryAddress(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return firstText(order.address, order.deliveryAddress, raw.address, raw.deliveryAddress, raw.customer?.address, 'No address captured');
}

function orderNo(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  return firstText(order.orderNumber, order.orderNo, order.displayId, order.id, 'Order');
}

function orderStage(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  return firstText(order.stage, order.status, order.storeStatus, 'waiting').toLowerCase();
}

function storeName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return firstText(order.store, order.storeName, raw.store, raw.storeName, raw.site, order.site, 'Store');
}

function createdAt(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return order.createdAt || raw.createdAt || order.timings?.createdAt || raw.timings?.createdAt || order.placedAt || raw.placedAt;
}

function stageStartedAt(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const timing = order.timings || raw.timings || {};
  return timing.stageStartedAt || timing.readyForDriverAt || timing.packedAt || timing.sentToStoreAt || timing.createdAt || createdAt(order);
}

function currentTimer(order, now) {
  return minutesSince(stageStartedAt(order), now);
}

function isClosed(order) {
  const stage = orderStage(order);
  return Boolean(order.archived || order.cancelled || order.delivered || CLOSED_STAGES.includes(stage));
}

function assignedDriverName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  return firstText(order.assignedDriverName, order.assignedDriver, order.driverName, order.assignment?.driverName, order.raw?.assignedDriverName);
}

function needsDriver(order) {
  const stage = orderStage(order);
  return ['ready', 'ready_for_driver', 'waiting', 'awaiting_store', 'packing'].includes(stage) && !assignedDriverName(order);
}

function overallTimer(order, now) {
  return minutesSince(createdAt(order) || stageStartedAt(order), now);
}

function riskLevel(order, now) {
  if (isClosed(order)) return 'closed';
  const mins = currentTimer(order, now);
  const overallMins = overallTimer(order, now);
  if (order.issueFlag || order.flags?.hasStockIssue || order.flags?.requiresApproval || overallMins >= 45 || mins >= 30) return 'critical';
  if (needsDriver(order) || overallMins >= 25 || mins >= 15) return 'attention';
  return 'healthy';
}

function riskCopy(order, now) {
  const mins = currentTimer(order, now);
  const overallMins = overallTimer(order, now);
  if (order.issueFlag || order.flags?.hasStockIssue || order.flags?.requiresApproval) return 'Needs operator decision';
  if (overallMins >= 45) return `${fmtMins(overallMins)} total age`;
  if (needsDriver(order)) return 'Driver needed';
  if (mins >= 30) return `${fmtMins(mins)} in status`;
  if (mins >= 15 || overallMins >= 25) return `${fmtMins(Math.max(mins, overallMins))} needs attention`;
  return 'On track';
}

function timelineRows(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const rows = [...safeArray(order.events), ...safeArray(order.timeline), ...safeArray(raw.timeline)];
  return rows
    .map((row, index) => ({
      id: row.id || index,
      title: firstText(row.title, row.text, row.message, row.type, 'Activity'),
      at: getDate(row.createdAt || row.at || row.clientAt),
    }))
    .sort((a, b) => (b.at?.getTime?.() || 0) - (a.at?.getTime?.() || 0))
    .slice(0, 7);
}

function driverLabel(driver = {}) {
  return firstText(driver.name, driver.displayName, driver.driverName, driver.id, 'Driver');
}

function driverStore(driver = {}) {
  return firstText(driver.store, driver.baseStore, driver.site, driver.currentStore);
}

function driverLoad(driver = {}) {
  return Number(driver.load ?? driver.activeRuns ?? driver.runCount ?? 0) || 0;
}

function isDriverOnline(driver = {}) {
  const status = String(driver.status || driver.availability || '').toLowerCase();
  return !status.includes('offline') && !status.includes('inactive');
}

function driverAvailable(driver = {}) {
  const status = String(driver.status || driver.availability || '').toLowerCase();
  return isDriverOnline(driver) && (driverLoad(driver) === 0 || status.includes('online') || status.includes('available'));
}


function topSuggestionForOrder(order, orders, drivers, stores, now) {
  return generateLinkSmartSuggestions({ order, orders, drivers, stores, now })[0] || null;
}

function buildSmartSuggestions({ selectedOrder, selectedRisk, selectedStore, storeResources, drivers, availableDrivers, selectedItems, now }) {
  if (!selectedOrder) return [];
  const storesByRisk = [...storeResources].sort((a, b) => (a.riskScore - b.riskScore) || a.orders - b.orders);
  const healthiestStore = storesByRisk.find((store) => store.store !== selectedStore && store.risk !== 'High') || storesByRisk.find((store) => store.store !== selectedStore);
  const selectedStoreResource = storeResources.find((store) => store.store === selectedStore);
  const nearestDriver = availableDrivers[0] || drivers.find(isDriverOnline) || drivers[0];
  const suggestions = [];

  if (needsDriver(selectedOrder)) {
    suggestions.push({
      tone: 'blue',
      title: 'Driver assignment available',
      problem: `${orderNo(selectedOrder)} is ${STAGE_LABELS[orderStage(selectedOrder)] || titleCase(orderStage(selectedOrder))} without an assigned driver.`,
      signal: nearestDriver ? `${driverLabel(nearestDriver)} is the best available driver signal right now.` : 'No available driver signal found.',
      impact: `Customer has been waiting ${fmtMins(currentTimer(selectedOrder, now))}.`,
      action: nearestDriver ? `Assign ${driverLabel(nearestDriver)}` : 'Review drivers',
      actionKey: 'assign',
    });
  }

  if (selectedStoreResource?.risk === 'High' && healthiestStore) {
    suggestions.push({
      tone: 'amber',
      title: 'Store pressure detected',
      problem: `${selectedStore} has ${selectedStoreResource.orders} active orders and ${selectedStoreResource.critical} critical risk${selectedStoreResource.critical === 1 ? '' : 's'}.`,
      signal: `${healthiestStore.store} has lower pressure with ${healthiestStore.orders} active order${healthiestStore.orders === 1 ? '' : 's'} and ${healthiestStore.nearbyDrivers} driver signal${healthiestStore.nearbyDrivers === 1 ? '' : 's'}.`,
      impact: 'Moving this order can reduce queue pressure before SLA breach spreads.',
      action: `Move to ${healthiestStore.store}`,
      actionKey: 'move',
      targetStore: healthiestStore.store,
    });
  }

  if (selectedItems.length) {
    const firstItem = selectedItems[0];
    suggestions.push({
      tone: 'purple',
      title: 'Product decision check',
      problem: `${itemName(firstItem)} is part of this order. If the store marks it unavailable, Link can trigger a swap or store move.`,
      signal: healthiestStore ? `Backup store candidate: ${healthiestStore.store}.` : 'No backup store candidate detected yet.',
      impact: 'This keeps the operator inside Control Tower instead of chasing stock updates manually.',
      action: 'Approve swap',
      actionKey: 'swap',
    });
  }

  if (selectedRisk === 'critical' && !suggestions.some((item) => item.actionKey === 'move') && healthiestStore) {
    suggestions.push({
      tone: 'red',
      title: 'Critical order intervention',
      problem: `${orderNo(selectedOrder)} has crossed operational tolerance.`,
      signal: `${healthiestStore.store} is the safest alternative resource based on current store pressure.`,
      impact: 'Immediate action reduces manual escalation and customer waiting time.',
      action: `Move to ${healthiestStore.store}`,
      actionKey: 'move',
      targetStore: healthiestStore.store,
    });
  }

  if (!suggestions.length) {
    suggestions.push({
      tone: 'green',
      title: 'No intervention required',
      problem: `${orderNo(selectedOrder)} is currently healthy.`,
      signal: `${selectedStore} has no active critical signal for this order.`,
      impact: 'Keep monitoring. No operator action is required right now.',
      action: 'Send update',
      actionKey: 'note',
    });
  }

  return suggestions.slice(0, 4);
}

export default function OnTimeControlTowerQueuePage() {
  const { orgId, orders = [], drivers = [], loading } = useOnTimeLiveData();
  const { user, claims } = useAuth();
  const [now] = useState(Date.now());
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [targetStore, setTargetStore] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const actor = useMemo(() => ({
    userId: user?.uid || '',
    email: user?.email || '',
    name: claims?.name || user?.displayName || user?.email || 'Control Tower',
  }), [claims?.name, user?.displayName, user?.email, user?.uid]);

  const stores = useMemo(() => {
    const names = new Set(FALLBACK_STORES);
    orders.forEach((order) => {
      const name = storeName(order);
      if (name && name !== 'Store') names.add(name);
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const decisionContext = useMemo(() => buildLinkDecisionContext({
    orders,
    drivers,
    stores,
    selectedOrderId: selectedId,
    now,
  }), [drivers, now, orders, selectedId, stores]);

  const sortedOrders = decisionContext.liveOrders;
  const counts = decisionContext.summary;

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return sortedOrders;
    return sortedOrders.filter((order) => decisionContext.orderScores.get(order.id)?.severity === filter);
  }, [decisionContext.orderScores, filter, sortedOrders]);

  const selectedOrder = useMemo(() => {
    const fromSelected = sortedOrders.find((order) => order.id === selectedId);
    if (fromSelected) return fromSelected;
    return filteredOrders[0] || sortedOrders[0] || null;
  }, [filteredOrders, selectedId, sortedOrders]);

  const predictionMap = useMemo(() => buildLinkPredictionMap(sortedOrders, {
    now,
    storeSignals: decisionContext.storeSignals,
    driverSignals: decisionContext.driverSignals,
  }), [decisionContext.driverSignals, decisionContext.storeSignals, now, sortedOrders]);

  const predictionSummary = useMemo(() => summarizeLinkPredictions(predictionMap), [predictionMap]);
  const selectedScore = selectedOrder ? decisionContext.orderScores.get(selectedOrder.id) : null;
  const selectedPrediction = selectedOrder ? predictionMap.get(selectedOrder.id) : null;
  const onlineDrivers = useMemo(() => drivers.filter(isDriverOnline), [drivers]);
  const availableDrivers = useMemo(() => drivers.filter(driverAvailable), [drivers]);

  const storeResources = useMemo(() => decisionContext.storeSignals.map((store) => ({
    store: store.store,
    orders: store.activeOrders,
    critical: store.criticalOrders,
    attention: store.attentionOrders,
    nearbyDrivers: store.onlineDrivers,
    availableDrivers: store.availableDrivers,
    riskScore: store.loadScore,
    risk: store.status === 'high' ? 'High' : store.status === 'attention' ? 'Attention' : 'Good',
  })), [decisionContext.storeSignals]);

  const selectedRisk = selectedScore?.severity || (selectedOrder ? riskLevel(selectedOrder, now) : 'healthy');
  const selectedItems = selectedOrder ? orderItems(selectedOrder) : [];
  const selectedTimeline = selectedOrder ? timelineRows(selectedOrder) : [];
  const selectedStore = selectedOrder ? storeName(selectedOrder) : 'Store';
  const selectedStoreResource = storeResources.find((store) => store.store === selectedStore);
  const suggestions = useMemo(() => {
    const baseSuggestions = generateLinkSmartSuggestions({
      order: selectedOrder,
      orders: sortedOrders,
      drivers,
      stores,
      now,
    }).map((suggestion) => ({
      ...suggestion,
      tone: suggestion.severity === 'critical' ? 'red' : suggestion.severity === 'attention' ? 'amber' : 'green',
      action: suggestion.actionLabel,
      actionKey: suggestion.type === 'assign_driver' ? 'assign' : suggestion.type === 'move_store' ? 'move' : suggestion.type === 'swap_product' ? 'swap' : 'note',
      targetStore: suggestion.payload?.targetStore,
    }));

    return rankRecoveryActions({
      order: selectedOrder,
      suggestions: baseSuggestions,
      prediction: selectedPrediction,
      storeSignals: decisionContext.storeSignals,
      driverSignals: decisionContext.driverSignals,
    });
  }, [decisionContext.driverSignals, decisionContext.storeSignals, drivers, now, selectedOrder, selectedPrediction, sortedOrders, stores]);

  const bestRecoveryAction = useMemo(() => summarizeBestRecoveryAction(suggestions), [suggestions]);

  function chosenDriver() {
    return drivers.find((driver) => String(driver.id) === String(driverId)) || availableDrivers[0] || onlineDrivers[0] || drivers[0];
  }

  async function runAction(key, fn) {
    if (!selectedOrder || !orgId) return;
    setBusy(key);
    setMessage('');
    try {
      await fn();
      setMessage('Action completed. Live order updated.');
    } catch (error) {
      console.error('[Control Tower]', error);
      setMessage(error?.message || 'Action failed.');
    } finally {
      setBusy('');
    }
  }

  function runSuggestedAction(suggestion) {
    if (!suggestion) return;
    if (suggestion.targetStore) setTargetStore(suggestion.targetStore);
    if (suggestion.actionKey === 'assign') {
      const driver = chosenDriver();
      if (driver?.id && !driverId) setDriverId(driver.id);
      runAction('assign', async () => {
        const chosen = chosenDriver();
        if (!chosen) throw new Error('No driver available.');
        await assignDriver({ orgId, orderId: selectedOrder.id, driverId: chosen.id || chosen.name, driverName: driverLabel(chosen), actor });
      });
      return;
    }
    if (suggestion.actionKey === 'move') {
      const store = suggestion.targetStore || targetStore;
      runAction('move', async () => {
        if (!store) throw new Error('Select a target store first.');
        await controlTowerSendToDifferentStore({ orgId, orderId: selectedOrder.id, order: selectedOrder, targetStore: store, reason: suggestion.title, actor });
      });
      return;
    }
    if (suggestion.actionKey === 'swap') {
      runAction('swap', () => controlTowerApproveAdjustment({ orgId, orderId: selectedOrder.id, actor }));
      return;
    }
    setNote(suggestion.problem || 'Control Tower update');
  }

  return (
    <div style={styles.page}>
      <header style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>CONTROL TOWER</div>
          <h1 style={styles.title}>Operational decision engine</h1>
          <p style={styles.subtitle}>What should I do next? Live orders, connected resources and smart suggestions in one workspace.</p>
        </div>
        <div style={styles.kpiGrid}>
          <Kpi label="Live Orders" value={counts.all} sub="active only" tone="blue" />
          <Kpi label="Critical" value={counts.critical} sub="act now" tone="red" />
          <Kpi label="Attention" value={counts.attention} sub="watch closely" tone="amber" />
          <Kpi label="Healthy" value={counts.healthy} sub="on track" tone="green" />
          <Kpi label="Drivers" value={onlineDrivers.length || drivers.length} sub="online" tone="cyan" />
          <Kpi label="SLA Risk" value={predictionSummary.predictedCritical} sub={`${predictionSummary.rising} rising`} tone="red" />
        </div>
      </header>

      <section style={styles.workspace}>
        <aside style={styles.ordersPanel}>
          <PanelTitle title="Live Orders" right={loading ? 'Loading' : `${filteredOrders.length}`} />
          <div style={styles.filters}>
            <button type="button" onClick={() => setFilter('all')} style={chipStyle(filter === 'all', 'blue')}>All {counts.all}</button>
            <button type="button" onClick={() => setFilter('critical')} style={chipStyle(filter === 'critical', 'red')}>Critical {counts.critical}</button>
            <button type="button" onClick={() => setFilter('attention')} style={chipStyle(filter === 'attention', 'amber')}>Attention {counts.attention}</button>
            <button type="button" onClick={() => setFilter('healthy')} style={chipStyle(filter === 'healthy', 'green')}>Healthy {counts.healthy}</button>
          </div>
          <div style={styles.orderList}>
            {filteredOrders.map((order) => {
              const score = decisionContext.orderScores.get(order.id);
              const risk = score?.severity || riskLevel(order, now);
              const suggestion = topSuggestionForOrder(order, sortedOrders, drivers, stores, now);
              const active = selectedOrder?.id === order.id;
              return (
                <button key={order.id} onClick={() => setSelectedId(order.id)} style={{ ...styles.orderCard, ...riskStyles[risk], ...(active ? styles.orderCardActive : null) }}>
                  <div style={styles.orderTop}>
                    <span style={{ ...styles.badge, ...badgeStyles[risk] }}>{risk === 'healthy' ? 'HEALTHY' : risk.toUpperCase()}</span>
                    <strong>{orderNo(order)}</strong>
                  </div>
                  <div style={styles.priorityRow}>
                    <span>Priority Score</span>
                    <strong>{score?.priority ?? 0}/100</strong>
                  </div>
                  <div style={styles.orderStore}>{storeName(order)}</div>
                  <div style={styles.orderCustomer}>{customerName(order)}</div>
                  <div style={styles.orderProblem}>{fmtMins(score?.stageMinutes ?? currentTimer(order, now))} · {(score?.reasons?.[0]) || riskCopy(order, now)}</div>
                  {predictionMap.get(order.id) && (
                    <div style={styles.predictionLine}>
                      SLA: {predictionMap.get(order.id).summary} · Recovery {predictionMap.get(order.id).recoveryChance}%
                    </div>
                  )}
                  {suggestion && <div style={styles.recommendationLine}>Recommended action: {suggestion.recommendedAction || suggestion.actionLabel}</div>}
                  <div style={styles.orderMeta}>
                    <span>{STAGE_LABELS[orderStage(order)] || titleCase(orderStage(order))}</span>
                    <span>{money(orderTotal(order))}</span>
                  </div>
                </button>
              );
            })}
            {!filteredOrders.length && <div style={styles.empty}>No live orders in this filter.</div>}
          </div>
        </aside>

        <main style={styles.centerPanel}>
          {selectedOrder ? (
            <>
              <div style={styles.workspaceHead}>
                <PanelTitle title="Operational Workspace" right={orderNo(selectedOrder)} />
                <div style={styles.problemStrip}>
                  <span style={{ ...styles.statusDot, background: selectedRisk === 'critical' ? '#ff3b53' : selectedRisk === 'attention' ? '#ffb020' : '#20e391' }} />
                  <strong>{selectedStore}</strong>
                  <span>{selectedScore?.reasons?.[0] || riskCopy(selectedOrder, now)}</span>
                  <span style={styles.priorityBadge}>Priority {selectedScore?.priority ?? 0}/100</span>
                  {selectedPrediction && <span style={styles.predictionBadge}>{selectedPrediction.summary}</span>}
                  {selectedPrediction && <span style={styles.recoveryBadge}>Recovery {selectedPrediction.recoveryChance}%</span>}
                  <span style={styles.age}>Order age: {fmtMins(minutesSince(createdAt(selectedOrder), now))}</span>
                </div>
              </div>

              <div style={styles.infoGrid}>
                <InfoCard title="Customer" rows={[customerName(selectedOrder), customerPhone(selectedOrder), deliveryAddress(selectedOrder)]} />
                <InfoCard title="Order" rows={[`Value: ${money(orderTotal(selectedOrder))}`, `Payment: ${firstText(selectedOrder.paymentMethod, selectedOrder.raw?.paymentMethod, selectedOrder.payment?.method, 'Not captured')}`, `Stage: ${STAGE_LABELS[orderStage(selectedOrder)] || titleCase(orderStage(selectedOrder))}`, selectedPrediction ? `SLA target: ${selectedPrediction.targetMinutes}m · ${selectedPrediction.breachLabel}` : 'SLA prediction pending']} />
                <InfoCard title="Assignment" rows={[`Store: ${selectedStore}`, `Driver: ${assignedDriverName(selectedOrder) || 'Unassigned'}`, `Available drivers: ${availableDrivers.length}`]} />
              </div>

              <div style={styles.detailsGrid}>
                <section style={styles.subPanel}>
                  <div style={styles.subTitle}>Items</div>
                  {selectedItems.slice(0, 7).map((item, index) => (
                    <div key={`${itemName(item)}-${index}`} style={styles.itemRow}>
                      <span>{itemQty(item)} × {itemName(item)}</span>
                      <strong>{money(itemPrice(item))}</strong>
                    </div>
                  ))}
                  {!selectedItems.length && <div style={styles.muted}>No items captured on this order.</div>}
                </section>
                <section style={styles.subPanel}>
                  <div style={styles.subTitle}>Timeline</div>
                  {selectedTimeline.map((row) => (
                    <div key={row.id} style={styles.timelineRow}>
                      <span>{row.at ? row.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Live'}</span>
                      <strong>{row.title}</strong>
                    </div>
                  ))}
                  {!selectedTimeline.length && <div style={styles.muted}>Timeline will populate as the order moves.</div>}
                </section>
              </div>

              <section style={styles.smartPanel}>
                <div style={styles.smartHeader}>
                  <div>
                    <div style={styles.eyebrowSmall}>SMART SUGGESTIONS</div>
                    <h2 style={styles.smartTitle}>What Link recommends next</h2>
                  </div>
                  <span style={styles.smartBadge}>SLA Prediction • Recovery Chance • Action</span>
                </div>
                {selectedPrediction && (
                  <div style={styles.predictionPanel}>
                    <div>
                      <strong>{selectedPrediction.summary}</strong>
                      <p>Risk trend: {titleCase(selectedPrediction.riskTrend)} · Predicted risk: {titleCase(selectedPrediction.predictedRisk)} · Current stage timer: {fmtMins(selectedPrediction.stageMinutes)}</p>
                    </div>
                    <div style={styles.predictionScore}>
                      <span>{bestRecoveryAction.recoveryChance || selectedPrediction.recoveryChance}%</span>
                      <small>Best recovery</small>
                    </div>
                    <div style={styles.bestActionStrip}>
                      <span>Best action</span>
                      <strong>{bestRecoveryAction.title}</strong>
                      <em>{bestRecoveryAction.label}</em>
                    </div>
                  </div>
                )}
                <div style={styles.suggestionGrid}>
                  {suggestions.map((suggestion, index) => (
                    <article key={`${suggestion.title}-${index}`} style={{ ...styles.suggestionCard, ...suggestionToneStyles[suggestion.tone] }}>
                      <div style={styles.suggestionTop}>
                        <strong>{suggestion.title}</strong>
                        <span>{index + 1}</span>
                      </div>
                      <div style={styles.decisionLine}><span>Problem</span><p>{suggestion.problem}</p></div>
                      <div style={styles.decisionLine}><span>Signal</span><p>{suggestion.signal}</p></div>
                      <div style={styles.decisionLine}><span>Impact</span><p>{suggestion.impact}</p></div>
                      <div style={styles.recoveryRow}>
                        <span>{suggestion.bestAction ? 'Best action' : `Rank #${suggestion.rank}`}</span>
                        <strong>{suggestion.recoveryLabel}</strong>
                      </div>
                      {suggestion.networkImpact && (
                        <div style={styles.networkImpact}>
                          <p>{suggestion.networkImpact.primary}</p>
                          <p>{suggestion.networkImpact.secondary}</p>
                          <strong>{suggestion.networkImpact.outcome}</strong>
                        </div>
                      )}
                      <div style={styles.recommendedAction}>Recommended action: <strong>{suggestion.recommendedAction || suggestion.action}</strong></div>
                      <button type="button" style={styles.suggestionBtn} disabled={!!busy} onClick={() => runSuggestedAction(suggestion)}>{busy === suggestion.actionKey ? 'Running...' : suggestion.action}</button>
                    </article>
                  ))}
                </div>
              </section>

              <section style={styles.actionsPanel}>
                <div style={styles.subTitle}>Decision Actions</div>
                <div style={styles.formRow}>
                  <select value={driverId} onChange={(event) => setDriverId(event.target.value)} style={styles.select}>
                    <option value="">Best available driver</option>
                    {drivers.map((driver) => <option key={driver.id || driver.name} value={driver.id}>{driverLabel(driver)}</option>)}
                  </select>
                  <button type="button" style={styles.primaryBtn} disabled={!!busy || !drivers.length} onClick={() => runAction('assign', async () => {
                    const driver = chosenDriver();
                    if (!driver) throw new Error('No driver available.');
                    await assignDriver({ orgId, orderId: selectedOrder.id, driverId: driver.id || driver.name, driverName: driverLabel(driver), actor });
                  })}>{busy === 'assign' ? 'Assigning...' : 'Assign Driver'}</button>
                </div>
                <div style={styles.formRow}>
                  <select value={targetStore} onChange={(event) => setTargetStore(event.target.value)} style={styles.select}>
                    <option value="">Select target store</option>
                    {stores.filter((store) => store !== selectedStore).map((store) => <option key={store} value={store}>{store}</option>)}
                  </select>
                  <button type="button" style={styles.actionBtn} disabled={!!busy || !targetStore} onClick={() => runAction('move', async () => {
                    const reason = window.prompt('Reason for moving this order?', `Operational decision from Control Tower: ${riskCopy(selectedOrder, now)}`);
                    if (!reason) throw new Error('Move cancelled.');
                    await controlTowerSendToDifferentStore({ orgId, orderId: selectedOrder.id, order: selectedOrder, targetStore, reason, actor });
                  })}>{busy === 'move' ? 'Moving...' : 'Move Store'}</button>
                </div>
                <div style={styles.buttonGrid}>
                  <button type="button" style={styles.actionBtn} disabled={!!busy} onClick={() => runAction('swap', () => controlTowerApproveAdjustment({ orgId, orderId: selectedOrder.id, actor }))}>Approve Swap</button>
                  <button type="button" style={styles.actionBtn} disabled={!!busy} onClick={() => runAction('payment', async () => {
                    const method = window.prompt('New payment method?', firstText(selectedOrder.paymentMethod, selectedOrder.payment?.method, 'Card'));
                    if (!method) throw new Error('Payment update cancelled.');
                    await updateDoc(doc(db, 'orgs', orgId, 'ontimeOrders', selectedOrder.id), { paymentMethod: method, 'payment.method': method, updatedAt: serverTimestamp() });
                  })}>Change Payment</button>
                  <button type="button" style={styles.actionBtn} disabled={!!busy} onClick={() => runAction('pause', () => updateOrderStage({ orgId, orderId: selectedOrder.id, stage: 'paused', actor, message: 'Order paused from Control Tower' }))}>Pause Order</button>
                  <button type="button" style={styles.dangerBtn} disabled={!!busy} onClick={() => runAction('cancel', async () => {
                    const reason = window.prompt('Cancellation reason?');
                    if (!reason) throw new Error('Cancellation stopped.');
                    await controlTowerCancelOrder({ orgId, orderId: selectedOrder.id, order: selectedOrder, reason, actor });
                  })}>Cancel Order</button>
                </div>
                <div style={styles.noteRow}>
                  <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Send note to order chat..." style={styles.noteInput} />
                  <button type="button" style={styles.primaryBtn} disabled={!!busy || !note.trim()} onClick={() => runAction('note', async () => {
                    await linkOrderSendMessage({ orgId, orderId: selectedOrder.id, text: note.trim(), actor, audience: 'operations' });
                    setNote('');
                  })}>Send</button>
                </div>
                {message && <div style={message.includes('failed') || message.includes('Error') ? styles.notice : styles.success}>{message}</div>}
              </section>
            </>
          ) : (
            <div style={styles.emptyLarge}>
              <h2>No live operational decisions</h2>
              <p>Delivered, cancelled and archived orders are excluded from Control Tower.</p>
            </div>
          )}
        </main>

        <aside style={styles.resourcesPanel}>
          <PanelTitle title="Resources" right="Live" />
          <ResourceSection title="Stores">
            {storeResources.map((store) => (
              <div key={store.store} style={styles.resourceCard}>
                <div>
                  <strong>{store.store}</strong>
                  <span>{store.orders} active orders</span>
                  <span>{store.nearbyDrivers} driver signals nearby</span>
                </div>
                <em style={store.risk === 'High' ? styles.redPill : store.risk === 'Attention' ? styles.amberPill : styles.greenPill}>{store.risk}</em>
              </div>
            ))}
          </ResourceSection>
          <ResourceSection title="Drivers">
            {drivers.slice(0, 8).map((driver) => {
              const load = driverLoad(driver);
              return (
                <div key={driver.id || driver.name} style={styles.resourceCard}>
                  <div>
                    <strong>{driverLabel(driver)}</strong>
                    <span>{load} active run{load === 1 ? '' : 's'}</span>
                    <span>{driverStore(driver) || driver.status || 'online'}</span>
                  </div>
                  <em style={load ? styles.amberPill : styles.greenPill}>{load ? 'Busy' : 'Available'}</em>
                </div>
              );
            })}
            {!drivers.length && <div style={styles.muted}>No driver signals available yet.</div>}
          </ResourceSection>
          <section style={styles.rightSmartBox}>
            <div style={styles.eyebrowSmall}>WHY THIS PAGE EXISTS</div>
            <h3>Control Tower answers: what should I do next?</h3>
            <p>Overview shows what is happening. Control Map shows where everything is. Control Tower turns live orders, stores and drivers into decisions.</p>
          </section>
        </aside>
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div style={{ ...styles.kpi, ...(tone === 'red' ? styles.kpiRed : tone === 'amber' ? styles.kpiAmber : tone === 'green' ? styles.kpiGreen : tone === 'cyan' ? styles.kpiCyan : null) }}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </div>
  );
}

function PanelTitle({ title, right }) {
  return <div style={styles.panelTitle}><h2 style={styles.panelHeading}>{title}</h2><span style={styles.panelCount}>{right}</span></div>;
}

function InfoCard({ title, rows }) {
  return <section style={styles.infoCard}><div style={styles.subTitle}>{title}</div>{rows.map((row, index) => <p key={index} style={styles.infoLine}>{row}</p>)}</section>;
}

function ResourceSection({ title, children }) {
  return <section style={styles.resourceSection}><div style={styles.resourceHeading}>{title}</div>{children}</section>;
}

function chipStyle(active, tone) {
  const toneMap = {
    blue: { color: '#dff4ff', borderColor: 'rgba(60,145,255,.45)', background: 'rgba(37,99,235,.34)' },
    red: { color: '#ff9aa7', borderColor: 'rgba(255,59,83,.42)' },
    amber: { color: '#ffd17a', borderColor: 'rgba(255,176,32,.42)' },
    green: { color: '#62f2b4', borderColor: 'rgba(32,227,145,.32)' },
  };
  return { ...styles.chipButton, ...(toneMap[tone] || {}), ...(active ? styles.chipButtonActive : null) };
}

const basePanel = {
  border: '1px solid rgba(94,231,255,.16)',
  background: 'linear-gradient(180deg, rgba(7,20,37,.94), rgba(4,12,24,.90))',
  borderRadius: 20,
  boxShadow: '0 24px 70px rgba(0,0,0,.32)',
};

const styles = {
  page: { padding: 24, color: '#eaf7ff', minHeight: 'calc(100vh - 170px)' },
  headerRow: { display: 'grid', gridTemplateColumns: 'minmax(340px, 1fr) minmax(640px, 920px)', gap: 18, alignItems: 'end', marginBottom: 16 },
  eyebrow: { color: '#5ee7ff', fontSize: 11, fontWeight: 900, letterSpacing: '.24em' },
  eyebrowSmall: { color: '#5ee7ff', fontSize: 10, fontWeight: 900, letterSpacing: '.18em' },
  title: { margin: '8px 0 6px', fontSize: 34, lineHeight: 1.02, fontWeight: 900 },
  subtitle: { margin: 0, color: '#9fb2c8', fontSize: 14, maxWidth: 680 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 10 },
  kpi: { ...basePanel, padding: '13px 14px', minHeight: 78, display: 'flex', flexDirection: 'column', justifyContent: 'center', borderColor: 'rgba(60,145,255,.24)' },
  kpiRed: { borderColor: 'rgba(255,59,83,.38)', background: 'linear-gradient(180deg, rgba(58,15,28,.70), rgba(7,20,37,.86))' },
  kpiAmber: { borderColor: 'rgba(255,176,32,.38)' },
  kpiGreen: { borderColor: 'rgba(32,227,145,.32)' },
  kpiCyan: { borderColor: 'rgba(94,231,255,.32)' },
  workspace: { display: 'grid', gridTemplateColumns: '320px minmax(640px, 1fr) 340px', gap: 14, alignItems: 'start' },
  ordersPanel: { ...basePanel, padding: 14, minHeight: 710 },
  centerPanel: { ...basePanel, padding: 14, minHeight: 710 },
  resourcesPanel: { ...basePanel, padding: 14, minHeight: 710 },
  panelTitle: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 },
  panelHeading: { margin: 0, fontSize: 18, fontWeight: 900 },
  panelCount: { color: '#5ee7ff', fontWeight: 900, fontSize: 13 },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  chipButton: { border: '1px solid rgba(148,163,184,.16)', borderRadius: 11, padding: '8px 10px', fontSize: 12, color: '#a9bdd3', background: 'rgba(255,255,255,.035)', fontWeight: 900, cursor: 'pointer' },
  chipButtonActive: { boxShadow: '0 0 0 1px rgba(94,231,255,.16), 0 0 20px rgba(35,129,255,.16)' },
  orderList: { display: 'grid', gap: 10, maxHeight: 605, overflowY: 'auto', paddingRight: 4 },
  orderCard: { textAlign: 'left', color: '#eaf7ff', width: '100%', borderRadius: 18, padding: 14, background: 'rgba(9,24,44,.74)', border: '1px solid rgba(94,231,255,.12)', cursor: 'pointer' },
  orderCardActive: { boxShadow: '0 0 0 1px rgba(94,231,255,.34), 0 0 32px rgba(35,129,255,.20)' },
  orderTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  badge: { fontSize: 10, padding: '5px 8px', borderRadius: 999, background: 'rgba(255,255,255,.07)', color: '#fff', fontWeight: 900 },
  orderStore: { marginTop: 12, fontSize: 17, fontWeight: 900 },
  orderCustomer: { color: '#b5c4d6', marginTop: 4, fontSize: 13 },
  orderProblem: { color: '#fff', marginTop: 8, fontWeight: 900, lineHeight: 1.35 },
  priorityRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, padding: '8px 10px', borderRadius: 12, border: '1px solid rgba(94,231,255,.14)', background: 'rgba(94,231,255,.045)', color: '#9fb2c8', fontSize: 12 },
  recommendationLine: { marginTop: 8, color: '#5ee7ff', fontSize: 12, fontWeight: 900, lineHeight: 1.35 },
  predictionLine: { marginTop: 8, color: '#ffd17a', fontSize: 12, fontWeight: 900, lineHeight: 1.35 },
  orderMeta: { display: 'flex', justifyContent: 'space-between', color: '#9fb2c8', marginTop: 10, fontSize: 12 },
  workspaceHead: { marginBottom: 12 },
  problemStrip: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid rgba(94,231,255,.16)', background: 'rgba(3,10,20,.42)', borderRadius: 16, padding: '12px 14px', flexWrap: 'wrap' },
  statusDot: { width: 9, height: 9, borderRadius: 99, boxShadow: '0 0 16px currentColor' },
  priorityBadge: { color: '#5ee7ff', border: '1px solid rgba(94,231,255,.28)', background: 'rgba(94,231,255,.07)', borderRadius: 999, padding: '5px 9px', fontSize: 12, fontWeight: 900 },
  predictionBadge: { color: '#ffd17a', border: '1px solid rgba(255,176,32,.30)', background: 'rgba(255,176,32,.08)', borderRadius: 999, padding: '5px 9px', fontSize: 12, fontWeight: 900 },
  recoveryBadge: { color: '#62f2b4', border: '1px solid rgba(32,227,145,.28)', background: 'rgba(32,227,145,.07)', borderRadius: 999, padding: '5px 9px', fontSize: 12, fontWeight: 900 },
  age: { marginLeft: 'auto', color: '#ff8795', fontWeight: 900 },
  infoGrid: { display: 'grid', gridTemplateColumns: '1.2fr .9fr .9fr', gap: 10, marginBottom: 10 },
  infoCard: { border: '1px solid rgba(148,163,184,.13)', background: 'rgba(255,255,255,.028)', borderRadius: 16, padding: 14, minHeight: 122 },
  infoLine: { margin: '6px 0', color: '#c6d4e4', fontSize: 13, lineHeight: 1.45 },
  subTitle: { fontSize: 13, fontWeight: 900, marginBottom: 10, color: '#eaf7ff' },
  detailsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 },
  subPanel: { border: '1px solid rgba(148,163,184,.13)', background: 'rgba(255,255,255,.028)', borderRadius: 16, padding: 14, minHeight: 210 },
  itemRow: { display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid rgba(148,163,184,.08)', padding: '8px 0', color: '#c6d4e4' },
  timelineRow: { display: 'grid', gridTemplateColumns: '72px 1fr', gap: 10, borderBottom: '1px solid rgba(148,163,184,.08)', padding: '8px 0', color: '#c6d4e4' },
  smartPanel: { border: '1px solid rgba(94,231,255,.18)', background: 'linear-gradient(135deg, rgba(6,21,41,.82), rgba(13,18,37,.76))', borderRadius: 18, padding: 14, marginBottom: 10, boxShadow: '0 0 34px rgba(94,231,255,.06)' },
  smartHeader: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', marginBottom: 12 },
  smartTitle: { margin: '4px 0 0', fontSize: 19, fontWeight: 900 },
  smartBadge: { border: '1px solid rgba(94,231,255,.24)', color: '#9eefff', borderRadius: 999, padding: '6px 9px', fontSize: 11, fontWeight: 900, background: 'rgba(94,231,255,.06)' },
  predictionPanel: { display: 'grid', gridTemplateColumns: '1fr 130px 190px', gap: 14, alignItems: 'center', border: '1px solid rgba(255,176,32,.22)', background: 'linear-gradient(135deg, rgba(255,176,32,.08), rgba(94,231,255,.035))', borderRadius: 16, padding: 13, marginBottom: 12 },
  predictionScore: { display: 'grid', placeItems: 'center', border: '1px solid rgba(32,227,145,.24)', background: 'rgba(32,227,145,.07)', borderRadius: 14, padding: 10, color: '#62f2b4', fontWeight: 900 },
  bestActionStrip: { border: '1px solid rgba(94,231,255,.20)', background: 'rgba(94,231,255,.055)', borderRadius: 14, padding: 10, display: 'grid', gap: 3, color: '#dff7ff' },
  recoveryRow: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginTop: 10, border: '1px solid rgba(32,227,145,.22)', background: 'rgba(32,227,145,.07)', borderRadius: 12, padding: '8px 10px', color: '#62f2b4', fontSize: 13, fontWeight: 900 },
  networkImpact: { marginTop: 9, border: '1px solid rgba(255,176,32,.16)', background: 'rgba(255,176,32,.055)', borderRadius: 12, padding: '9px 10px', color: '#c6d4e4', fontSize: 12, lineHeight: 1.45 },
  suggestionGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
  suggestionCard: { border: '1px solid rgba(94,231,255,.14)', background: 'rgba(1,8,18,.50)', borderRadius: 16, padding: 13 },
  suggestionTop: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 8 },
  decisionLine: { borderTop: '1px solid rgba(148,163,184,.08)', paddingTop: 8, marginTop: 8 },
  recommendedAction: { marginTop: 10, border: '1px solid rgba(94,231,255,.18)', background: 'rgba(94,231,255,.07)', borderRadius: 12, padding: '9px 10px', color: '#dff7ff', fontSize: 13 },
  suggestionBtn: { width: '100%', marginTop: 8, background: 'linear-gradient(135deg, #0ea5ff, #2563eb)', color: '#fff', border: 0, borderRadius: 12, padding: '10px 12px', fontWeight: 900, cursor: 'pointer' },
  actionsPanel: { border: '1px solid rgba(94,231,255,.14)', background: 'rgba(4,13,25,.64)', borderRadius: 16, padding: 14 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 150px', gap: 10, marginBottom: 10 },
  buttonGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 10 },
  select: { background: '#07182c', color: '#eaf7ff', border: '1px solid rgba(94,231,255,.18)', borderRadius: 12, padding: '10px 12px', outline: 'none' },
  primaryBtn: { background: 'linear-gradient(135deg, #0ea5ff, #2563eb)', color: '#fff', border: 0, borderRadius: 12, padding: '10px 12px', fontWeight: 900, cursor: 'pointer' },
  actionBtn: { background: 'rgba(37,99,235,.16)', color: '#dff4ff', border: '1px solid rgba(60,145,255,.35)', borderRadius: 12, padding: '10px 12px', fontWeight: 900, cursor: 'pointer' },
  dangerBtn: { background: 'rgba(239,68,68,.14)', color: '#ffb4bd', border: '1px solid rgba(255,59,83,.36)', borderRadius: 12, padding: '10px 12px', fontWeight: 900, cursor: 'pointer' },
  noteRow: { display: 'grid', gridTemplateColumns: '1fr 90px', gap: 10 },
  noteInput: { background: '#07182c', color: '#eaf7ff', border: '1px solid rgba(94,231,255,.18)', borderRadius: 12, padding: '10px 12px', outline: 'none' },
  resourceSection: { borderTop: '1px solid rgba(148,163,184,.12)', paddingTop: 14, marginTop: 14 },
  resourceHeading: { fontSize: 15, fontWeight: 900, color: '#5ee7ff', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 },
  resourceCard: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: 12, borderRadius: 15, border: '1px solid rgba(148,163,184,.11)', background: 'rgba(255,255,255,.03)', marginBottom: 8 },
  redPill: { color: '#ff8795', border: '1px solid rgba(255,59,83,.25)', background: 'rgba(255,59,83,.08)', borderRadius: 999, padding: '5px 8px', fontStyle: 'normal', fontWeight: 900 },
  amberPill: { color: '#ffd17a', border: '1px solid rgba(255,176,32,.25)', background: 'rgba(255,176,32,.08)', borderRadius: 999, padding: '5px 8px', fontStyle: 'normal', fontWeight: 900 },
  greenPill: { color: '#62f2b4', border: '1px solid rgba(32,227,145,.25)', background: 'rgba(32,227,145,.08)', borderRadius: 999, padding: '5px 8px', fontStyle: 'normal', fontWeight: 900 },
  rightSmartBox: { border: '1px solid rgba(94,231,255,.14)', background: 'rgba(94,231,255,.045)', borderRadius: 16, padding: 14, marginTop: 14, color: '#c6d4e4' },
  muted: { color: '#8093aa', fontSize: 13 },
  empty: { color: '#9fb2c8', border: '1px dashed rgba(148,163,184,.2)', padding: 18, borderRadius: 14 },
  emptyLarge: { color: '#9fb2c8', border: '1px dashed rgba(148,163,184,.2)', padding: 28, borderRadius: 18, minHeight: 420, display: 'grid', placeContent: 'center', textAlign: 'center' },
  success: { marginTop: 10, color: '#62f2b4', fontWeight: 900 },
  notice: { marginTop: 10, color: '#ffd17a', fontWeight: 900 },
};

const riskStyles = {
  critical: { borderColor: 'rgba(255,59,83,.72)', background: 'linear-gradient(135deg, rgba(74,19,32,.86), rgba(9,24,44,.74))' },
  attention: { borderColor: 'rgba(255,176,32,.72)', background: 'linear-gradient(135deg, rgba(65,43,10,.70), rgba(9,24,44,.74))' },
  healthy: { borderColor: 'rgba(32,227,145,.45)' },
};

const badgeStyles = {
  critical: { color: '#ff9aa7', background: 'rgba(255,59,83,.18)' },
  attention: { color: '#ffd17a', background: 'rgba(255,176,32,.16)' },
  healthy: { color: '#62f2b4', background: 'rgba(32,227,145,.12)' },
};

const suggestionToneStyles = {
  red: { borderColor: 'rgba(255,59,83,.28)', background: 'linear-gradient(135deg, rgba(74,19,32,.52), rgba(1,8,18,.50))' },
  amber: { borderColor: 'rgba(255,176,32,.30)', background: 'linear-gradient(135deg, rgba(65,43,10,.44), rgba(1,8,18,.50))' },
  blue: { borderColor: 'rgba(60,145,255,.30)', background: 'linear-gradient(135deg, rgba(12,45,92,.52), rgba(1,8,18,.50))' },
  purple: { borderColor: 'rgba(168,85,247,.28)', background: 'linear-gradient(135deg, rgba(47,25,82,.50), rgba(1,8,18,.50))' },
  green: { borderColor: 'rgba(32,227,145,.24)', background: 'linear-gradient(135deg, rgba(8,57,40,.44), rgba(1,8,18,.50))' },
};
